import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { reviewPolicyApi } from '../api';
import type {
  ReviewPolicyFinding,
  ReviewPolicyReport,
  ReviewPolicySeverity,
} from '../types';

export const SEVERITY_COLORS: Record<ReviewPolicySeverity, { bg: string; border: string; text: string; dot: string }> = {
  BLOCKER: { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c', dot: '#dc2626' },
  FLAG: { bg: '#fffbeb', border: '#fde68a', text: '#b45309', dot: '#d97706' },
  ADVISORY: { bg: '#f9fafb', border: '#e5e7eb', text: '#6b7280', dot: '#9ca3af' },
};

const SEVERITY_ORDER: ReviewPolicySeverity[] = ['BLOCKER', 'FLAG', 'ADVISORY'];

interface ReviewIssuesPanelProps {
  docId: string | null;
  isOpen: boolean;
  /** Bump to trigger a refetch while the panel is open (e.g. after saves). */
  refreshSignal?: number;
  onNavigate?: (finding: ReviewPolicyFinding) => void;
}

const ReviewIssuesPanel: React.FC<ReviewIssuesPanelProps> = ({
  docId,
  isOpen,
  refreshSignal = 0,
  onNavigate,
}) => {
  const [report, setReport] = useState<ReviewPolicyReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signoff, setSignoff] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<ReviewPolicySeverity | null>(null);
  const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

  const fetchReport = useCallback(async () => {
    if (!docId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await reviewPolicyApi.getReport(docId, { signoff });
      setReport(data);
    } catch (err) {
      console.error('Failed to load review policy report:', err);
      setError('Failed to load review issues');
    } finally {
      setLoading(false);
    }
  }, [docId, signoff]);

  useEffect(() => {
    if (isOpen && docId) {
      void fetchReport();
    }
  }, [isOpen, docId, signoff, refreshSignal, fetchReport]);

  const findings = report?.findings ?? [];

  const grouped = useMemo(() => {
    const bySeverity = new Map<ReviewPolicySeverity, Map<string, ReviewPolicyFinding[]>>();
    for (const sev of SEVERITY_ORDER) bySeverity.set(sev, new Map());
    for (const f of findings) {
      if (severityFilter && f.severity !== severityFilter) continue;
      const ruleMap = bySeverity.get(f.severity);
      if (!ruleMap) continue;
      const list = ruleMap.get(f.rule) ?? [];
      list.push(f);
      ruleMap.set(f.rule, list);
    }
    return bySeverity;
  }, [findings, severityFilter]);

  const toggleRule = (key: string) => {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (!docId) {
    return <div style={{ padding: 16, fontSize: 12, color: '#6b7280' }}>No document selected.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', fontSize: 12 }}>
      {/* Summary header */}
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
        }}>
        {SEVERITY_ORDER.map((sev) => {
          const count =
            sev === 'BLOCKER'
              ? report?.summary.blockers ?? 0
              : sev === 'FLAG'
                ? report?.summary.flags ?? 0
                : report?.summary.advisories ?? 0;
          const c = SEVERITY_COLORS[sev];
          const active = severityFilter === sev;
          return (
            <button
              key={sev}
              type="button"
              onClick={() => setSeverityFilter(active ? null : sev)}
              title={`Show only ${sev} findings`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                borderRadius: 999,
                border: `1px solid ${active ? c.dot : c.border}`,
                backgroundColor: c.bg,
                color: c.text,
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: c.dot }} />
              {count} {sev.toLowerCase()}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={() => void fetchReport()}
          disabled={loading}
          style={{
            borderRadius: 7,
            border: '1px solid rgba(0,0,0,0.08)',
            backgroundColor: 'white',
            padding: '4px 10px',
            fontSize: 11,
            fontWeight: 600,
            color: '#334155',
            cursor: loading ? 'wait' : 'pointer',
          }}>
          {loading ? 'Checking…' : 'Re-check'}
        </button>
      </div>

      {/* Sign-off toggle */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          borderBottom: '1px solid #f3f4f6',
          color: '#374151',
          cursor: 'pointer',
        }}>
        <input
          type="checkbox"
          checked={signoff}
          onChange={(e) => setSignoff(e.target.checked)}
          style={{ margin: 0 }}
        />
        <span>
          <strong>Sign-off mode</strong> — include gate rules (segment review, sequence order, isolation
          valves)
        </span>
      </label>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px' }}>
        {error && (
          <div
            style={{
              padding: 10,
              borderRadius: 8,
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              marginBottom: 8,
            }}>
            {error}
          </div>
        )}
        {!error && !loading && report && findings.length === 0 && (
          <div
            style={{
              padding: 14,
              borderRadius: 8,
              backgroundColor: '#ecfdf3',
              border: '1px solid #a7f3d0',
              color: '#047857',
              fontWeight: 600,
            }}>
            No policy issues found. This sheet passes review policy v{report.summary.policy_version}.
          </div>
        )}
        {SEVERITY_ORDER.map((sev) => {
          const ruleMap = grouped.get(sev);
          if (!ruleMap || ruleMap.size === 0) return null;
          const c = SEVERITY_COLORS[sev];
          return (
            <div key={sev} style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  color: c.text,
                  margin: '6px 2px',
                }}>
                {sev}
              </div>
              {Array.from(ruleMap.entries()).map(([rule, ruleFindings]) => {
                const key = `${sev}:${rule}`;
                const expanded = expandedRules.has(key);
                return (
                  <div
                    key={key}
                    style={{
                      border: `1px solid ${c.border}`,
                      borderRadius: 8,
                      marginBottom: 6,
                      backgroundColor: 'white',
                      overflow: 'hidden',
                    }}>
                    <button
                      type="button"
                      onClick={() => toggleRule(key)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        border: 'none',
                        backgroundColor: c.bg,
                        padding: '7px 10px',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}>
                      <span
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 11,
                          fontWeight: 700,
                          color: c.text,
                        }}>
                        {rule}
                      </span>
                      <span style={{ flex: 1, color: '#6b7280', fontSize: 11 }}>
                        {ruleFindings[0]?.message.slice(0, 46)}
                        {(ruleFindings[0]?.message.length ?? 0) > 46 ? '…' : ''}
                      </span>
                      <span
                        style={{
                          borderRadius: 999,
                          backgroundColor: c.dot,
                          color: 'white',
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '1px 7px',
                        }}>
                        {ruleFindings.length}
                      </span>
                      <span style={{ color: '#9ca3af', fontSize: 10 }}>{expanded ? '▾' : '▸'}</span>
                    </button>
                    {expanded && (
                      <div>
                        {ruleFindings.map((f, idx) => {
                          const clickable = Boolean(onNavigate && f.target_id);
                          return (
                            <div
                              key={`${f.target_id ?? f.target_ref ?? 'doc'}-${idx}`}
                              onClick={clickable ? () => onNavigate?.(f) : undefined}
                              title={clickable ? 'Go to this object on the P&ID' : undefined}
                              style={{
                                display: 'flex',
                                gap: 8,
                                padding: '6px 10px',
                                borderTop: '1px solid #f3f4f6',
                                cursor: clickable ? 'pointer' : 'default',
                                alignItems: 'baseline',
                              }}>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: clickable ? '#2563eb' : '#374151',
                                  whiteSpace: 'nowrap',
                                  textDecoration: clickable ? 'underline' : 'none',
                                }}>
                                {f.target_ref || f.target_kind}
                              </span>
                              <span style={{ color: '#4b5563' }}>{f.message}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div
        style={{
          padding: '7px 14px',
          borderTop: '1px solid #e5e7eb',
          color: '#9ca3af',
          fontSize: 10,
        }}>
        Review policy v{report?.summary.policy_version ?? '—'} · {report?.summary.total ?? 0} findings ·
        blockers must be resolved before sign-off
      </div>
    </div>
  );
};

export default ReviewIssuesPanel;
